import * as React from 'react'

import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import { useIsMapStyleLoaded } from './MapCanvas'

const TRAFFIC_SOURCE = 'mapbox-traffic'
const TRAFFIC_LAYER = 'traffic-layer'
const TRAFFIC_URL = 'mapbox://mapbox.mapbox-traffic-v1'

// The width Workflows and Deliveries have drawn traffic at since the tilesets work.
const TRAFFIC_LINE_WIDTH = 6

const CONGESTION_COLOR = [
  'match',
  ['get', 'congestion'],
  'low',
  '#28d760',
  'moderate',
  '#ffff00',
  'heavy',
  '#ffcb32',
  'severe',
  '#ff0003',
  '#000000',
]

const TERRAIN_SOURCE_ID = 'terrain'
const TERRAIN_URL = 'mapbox://mapbox.terrain-rgb'
const TERRAIN_EXAGGERATION = 1.5

interface IProps {
  isTrafficEnabled?: boolean
  isTerrainEnabled?: boolean
  isThreeDEnabled?: boolean
  shouldHideLabels?: boolean
}

// Only traffic is a layer; terrain is a style property and 3D/labels are basemap config, so those
// go through the map instead.
const MapLayers: React.FC<IProps> = ({
  isTrafficEnabled,
  isTerrainEnabled,
  isThreeDEnabled,
  shouldHideLabels,
}) => {
  const { current: mapRef } = useMap()
  const isStyleLoaded = useIsMapStyleLoaded()

  // Its own effect: a label or 3D toggle must not rewrite the terrain.
  React.useEffect(() => {
    const map = mapRef?.getMap?.()
    // Undefined keeps "no control offered" out of it. isStyleLoaded is a dependency, not a guard:
    // a basemap swap drops the DEM source, so the terrain has to be written again.
    if (!map || !isStyleLoaded || isTerrainEnabled === undefined) {
      return
    }

    const applyTerrain = () => {
      // A moving camera fires its events after this point and before the style is evaluated, and
      // a render they start can remove a source. Only a write that creates a terrain is at risk:
      // enabling, or clearing on a globe, which drapes onto a placeholder terrain. Clearing a flat
      // map just deletes it, so it lands at once. Landing first also spares the fly the DEM loads.
      const createsTerrain =
        isTerrainEnabled || map.getProjection().name === 'globe'
      if (createsTerrain && map.isMoving()) {
        map.once('renderstart', applyTerrain)
        map.triggerRepaint()
        return
      }
      try {
        if (isTerrainEnabled) {
          // On first enable only: the DEM costs a TileJSON request per map, and most maps never ask.
          if (!map.getSource(TERRAIN_SOURCE_ID)) {
            map.addSource(TERRAIN_SOURCE_ID, {
              type: 'raster-dem',
              url: TERRAIN_URL,
            })
          }
          map.setTerrain({
            source: TERRAIN_SOURCE_ID,
            exaggeration: TERRAIN_EXAGGERATION,
          })
        } else {
          // Not just our own source: the v3 basemap ships terrain of its own, so off has to clear
          // whatever is set.
          map.setTerrain(null)
        }
      } catch (error) {
        console.warn('[MapLayers] could not apply the terrain:', error)
      }
    }
    // Deferred to the start of the next frame, which evaluates the style in the same pass. Written
    // mid-frame (a styledata listener can flush this effect), it leaves style state unevaluated for
    // the rest of that frame: the terrain itself before mapbox-gl 3.31 (mapbox-gl-js#13497), the
    // basemap's model layers still in 3.32.
    map.once('renderstart', applyTerrain)
    map.triggerRepaint()
    return () => {
      map.off('renderstart', applyTerrain)
    }
  }, [mapRef, isStyleLoaded, isTerrainEnabled])

  React.useEffect(() => {
    const map = mapRef?.getMap?.()
    // Every call below needs a loaded style; the flag is already a dependency, so a load in
    // progress is re-applied when it flips.
    if (!map || !isStyleLoaded) {
      return
    }

    try {
      // setConfigProperty throws on a style with no basemap import.
      if (!map.getConfig('basemap')) {
        return
      }
      // undefined is not false: a surface offering no control keeps the style's own default.
      if (isThreeDEnabled !== undefined) {
        map.setConfigProperty('basemap', 'show3dObjects', isThreeDEnabled)
      }
      if (shouldHideLabels !== undefined) {
        map.setConfigProperty('basemap', 'showPlaceLabels', !shouldHideLabels)
        map.setConfigProperty(
          'basemap',
          'showPointOfInterestLabels',
          !shouldHideLabels,
        )
      }
    } catch (error) {
      // Usually mid-style-load and retried on the next report, but a permanent failure would
      // otherwise disable two settings in silence.
      console.warn('[MapLayers] could not apply the basemap settings:', error)
    }
    // isStyleLoaded is a dependency, not a guard: a basemap swap resets the config, so it has to
    // be written again.
  }, [mapRef, isStyleLoaded, isThreeDEnabled, shouldHideLabels])

  return (
    <>
      {isTrafficEnabled && (
        <Source id={TRAFFIC_SOURCE} type="vector" url={TRAFFIC_URL}>
          <Layer
            id={TRAFFIC_LAYER}
            type="line"
            source={TRAFFIC_SOURCE}
            source-layer="traffic"
            // Above the road casing, below labels and 3D models so buildings occlude it. Unslotted
            // draws over everything.
            slot="middle"
            paint={{
              'line-width': TRAFFIC_LINE_WIDTH,
              'line-color': CONGESTION_COLOR as never,
            }}
          />
        </Source>
      )}
    </>
  )
}

export default MapLayers
